import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * Standard premium product catalog card.
 *
 * Spec:
 * - Card width: 320px (max), responsive grid handles layout
 * - Image area: 300×300px square canvas, object-contain, overflow-hidden
 * - Hover: image scale(1.12) with transition 0.3s ease
 * - Structure: large centered photo · product name · item code · features · CTA button
 */

const CARD_WIDTH_PX = 320;
const IMAGE_CANVAS_PX = 300;

// Static motion configs — module-level constants avoid re-creating identical
// objects each render, keeping framer-motion happy across the catalog.
const CARD_INITIAL = { opacity: 0, y: 18 };
const CARD_WHILE_IN_VIEW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, amount: 0.15 };
const CARD_TRANSITION = { duration: 0.5 };

const ProductCatalogCard = ({
  name,
  code,
  image,
  features = [],
  link,
  badge,
  onQuote,
  testIdSlug,
}) => {
  return (
    <motion.article
      initial={CARD_INITIAL}
      whileInView={CARD_WHILE_IN_VIEW}
      viewport={CARD_VIEWPORT}
      transition={CARD_TRANSITION}
      className="bg-white rounded-2xl shadow-[0_2px_18px_-6px_rgba(17,17,17,0.06)] hover:shadow-[0_20px_56px_-22px_rgba(17,17,17,0.20)] transition-shadow duration-500 flex flex-col h-full w-full mx-auto overflow-hidden"
      style={{ maxWidth: `${CARD_WIDTH_PX}px` }}
      data-testid={testIdSlug ? `catalog-card-${testIdSlug}` : undefined}
    >
      <div className="p-5 flex flex-col h-full">
        {/* Image canvas: exact 300×300 square, contain, overflow hidden, hover scale */}
        <Link
          to={link}
          className="block relative group/img"
          aria-label={`Ver ${name}`}
        >
          {badge && (
            <span className="absolute top-3 left-3 z-10 bg-[var(--mn-gold)] text-white text-[0.6rem] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-sm shadow-sm">
              {badge}
            </span>
          )}
          <div
            className="rounded-xl flex items-center justify-center overflow-hidden mx-auto"
            style={{
              background: "#F5F5F5",
              width: "100%",
              maxWidth: `${IMAGE_CANVAS_PX}px`,
              aspectRatio: "1 / 1",
              padding: "16px",
            }}
          >
            <img
              src={image}
              alt={name}
              loading="lazy"
              className="max-w-full max-h-full w-auto h-auto object-contain"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                transition: "transform 0.3s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.12)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            />
          </div>
        </Link>

        {/* Title */}
        <header className="mt-5">
          <Link to={link} className="block group/title">
            <h3 className="font-serif-display font-extrabold text-[1.1rem] leading-tight text-[var(--mn-black)] tracking-[-0.005em] group-hover/title:text-[var(--mn-gold)] transition-colors line-clamp-2">
              {name}
            </h3>
          </Link>
          {code && (
            <p className="text-[0.62rem] uppercase tracking-[0.24em] font-semibold text-[var(--mn-gold)] mt-1.5">
              {code}
            </p>
          )}
        </header>

        {/* Features list */}
        {features.length > 0 && (
          <ul className="mt-4 space-y-1.5 text-[0.78rem] text-[var(--mn-dark)] leading-relaxed flex-1">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <span className="text-[var(--mn-gold)] mt-[3px]">·</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}

        {/* CTA */}
        {onQuote && (
          <button
            type="button"
            onClick={() => onQuote(name)}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 px-4 bg-[var(--mn-gold)] text-white text-[0.72rem] font-bold uppercase tracking-[0.18em] rounded-lg hover:bg-[var(--mn-gold-hover)] transition-colors"
            data-testid={testIdSlug ? `catalog-quote-${testIdSlug}` : undefined}
          >
            <i className="fa-regular fa-paper-plane text-[0.8rem]" />
            Solicitar Orçamento
          </button>
        )}
      </div>
    </motion.article>
  );
};

export default ProductCatalogCard;

/**
 * Standard responsive grid wrapper for ProductCatalogCard.
 * 4 cols desktop · 2 cols tablet · 1 col mobile.
 */
export const ProductCatalogGrid = ({ children, testId }) => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7 auto-rows-fr place-items-stretch"
    data-testid={testId}
  >
    {children}
  </div>
);
